// ---------------------------------------------------------------------------
// Capa efetiva de um exemplar da estante — resolvedor ÚNICO.
//
// Blueprint: `docs/systems/user-book-cover.md`.
//
// ── Por que a resolução acontece na HIDRATAÇÃO, e não na renderização ───────
//
// Medido em 31/08: ~25 superfícies leem `userBook.coverUrl` direto (estante do
// dono e a pública, rails da Home, hero de leitura, grades do perfil, mosaico
// de listas, smart lists, onboarding, card exportável, `AlsoReadSection`…).
// Passar um resolvedor por cada uma seria a doença do BUG-81/BUG-87 em escala
// de 25 chances de esquecer — e a 26ª superfície nasceria errada, sem nada
// ficar vermelho.
//
// Medido também: existem 6 pontos de HIDRATAÇÃO, todos com a mesma forma
// (`{ id, ...docSnap.data() } as UserBook`). Resolver ali faz as 25 superfícies
// mostrarem a capa custom sem uma linha de mudança em nenhuma delas, e torna
// esquecer inexpressável em vez de improvável.
//
// ── O preço, e como ele é pago ──────────────────────────────────────────────
//
// `coverUrl` passa a ser o valor EFETIVO, e quem decide sobre a capa do
// CATÁLOGO precisa do cru. Ele continua disponível, com nome próprio:
// `catalogCoverUrl`. Dois consumidores o usam — `computeUserBookHealPatch`
// (BUG-171) e `shelfCoverBackfill` —, e nenhum dos dois pode ler o efetivo: um
// livro com capa custom pareceria "já curado" e nunca receberia a capa do
// catálogo por baixo, transformando "voltar ao catálogo" numa caixa vazia.
//
// ⚠️ `catalogCoverUrl` é DERIVADO em memória e **não existe no Firestore**.
// ---------------------------------------------------------------------------

/** Origens possíveis de uma capa escolhida pelo usuário. */
export const CUSTOM_COVER_SOURCES = ["upload", "edition"] as const;
export type CustomCoverSource = (typeof CUSTOM_COVER_SOURCES)[number];

/**
 * Quantas capas de UPLOAD a estante do free carrega ao mesmo tempo.
 *
 * ⚠️ **Espelho** de `FREE_CUSTOM_COVER_UPLOADS` em
 * `functions/src/books/userBookCover.ts`, e o servidor é quem manda — este
 * número existe só para a tela avisar antes, e há teste de paridade lendo o
 * fonte do servidor (mesma forma do `proShopDiscountParity`). Divergir faz a
 * UI prometer uma vaga que a callable recusa.
 *
 * ⚠️ A capa de OUTRA EDIÇÃO não entra nesta conta: ela é conteúdo do catálogo,
 * custa zero e é livre para todo mundo. O teto é do upload, que é o que a
 * pessoa produz e o que aparece como acervo curado no perfil dela.
 */
export const FREE_CUSTOM_COVER_UPLOADS = 1;

/**
 * Subconjunto de `user_books` de que a resolução depende. Existe como tipo
 * próprio para o resolvedor ser testável sem montar um `UserBook` inteiro.
 */
export interface UserBookCoverFields {
  /** Capa do CATÁLOGO, denormalizada. Escrita pela auto-cura e pelo import. */
  coverUrl?: string | null;
  /** Capa escolhida pelo usuário. Client-locked — só `setUserBookCover` grava. */
  customCoverUrl?: string | null;
}

/** Trata `""`, `null` e `undefined` como ausência. */
function present(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * A capa que TODA superfície de coleção deve mostrar.
 *
 * Precedência: escolha do usuário > catálogo. Nunca o contrário — a capa custom
 * é a única das duas que alguém pediu explicitamente, e o catálogo pode mudar
 * embaixo dela a qualquer enrichment.
 *
 * Devolve `""` (e não `null`) na ausência das duas porque é isso que os
 * primitivos de capa já tratam como "sem capa" hoje.
 */
export function resolveUserBookCover(fields: UserBookCoverFields | null | undefined): string {
  if (!fields) return "";
  return present(fields.customCoverUrl) ?? present(fields.coverUrl) ?? "";
}

/** `true` quando o exemplar tem capa escolhida pelo usuário. */
export function hasCustomCover(fields: UserBookCoverFields | null | undefined): boolean {
  return !!fields && present(fields.customCoverUrl) !== null;
}

/**
 * Aplica a resolução sobre o objeto cru do Firestore.
 *
 * É o ÚNICO lugar que decide, e por isso é ele que os 6 pontos de hidratação
 * chamam. Genérico no shape para servir tanto o `UserBook` completo quanto o
 * `UserBookReadingDoc` estreito da faixa de amigos.
 */
export function applyCoverResolution<T extends UserBookCoverFields>(
  raw: T,
): T & { coverUrl: string; catalogCoverUrl: string } {
  const catalogCoverUrl = present(raw.coverUrl) ?? "";
  return {
    ...raw,
    coverUrl: resolveUserBookCover(raw),
    catalogCoverUrl,
  };
}
